import { cn } from '@bridebook/aura';
import { useId } from 'react';
import styles from './planner-face.module.css';

/**
 * How the face moves on its own. `rest` blinks every few seconds, `wander` also looks
 * around now and then, and `think` blinks while the eyes look up to one side.
 */
export type PlannerFaceLook = 'rest' | 'wander' | 'think';

const LOOK_CLASS: Record<PlannerFaceLook, string> = {
  rest: styles.rest,
  wander: cn(styles.rest, styles.wander),
  think: cn(styles.rest, styles.think),
};

// Put on the element whose mouse hover makes BB blink twice
export const PLANNER_FACE_HOVER_CLASS = styles.hoverHost;

type PlannerFaceProps = {
  size: number;
  // Still when not set
  look?: PlannerFaceLook;
  className?: string;
};

const INK = '#1a2c40';

/**
 * The planner's logo: BB's face on the Bridebook bubble, in soft indigo. An inline SVG, so
 * each copy has its own gradient id and the eyes can move. The eyes carry a `data-part`
 * name for the looks: eyes, with eye-left and eye-right inside. Every look runs on CSS
 * and stays still for reduced motion.
 */
export const PlannerFace = ({ size, look, className }: PlannerFaceProps) => {
  const gradient = useId();

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      // The bubble's bottom edge dips just past the box
      overflow="visible"
      className={cn(styles.face, look && LOOK_CLASS[look], className)}
      aria-hidden>
      <defs>
        <radialGradient
          id={gradient}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(17.3229 16.532 -16.5288 17.3235 6.67714 6.66667)"
          gradientUnits="userSpaceOnUse">
          <stop stopColor="#edeefc" />
          <stop offset="0.495192" stopColor="#b8baf4" />
          <stop offset="0.774038" stopColor="#dbdcf9" />
        </radialGradient>
      </defs>
      <path
        d="M5.06201 1.07456C-2.40515 3.75091 0.308643 19.1457 4.22724 22.5279C8.14618 25.9101 24.4159 21.8293 23.6506 13.4056C23.0074 6.32591 17.1693 5.90663 14.5679 6.73192C14.2253 4.43324 11.5137 -1.23781 5.06201 1.07456Z"
        fill={`url(#${gradient})`}
      />
      {/* Each eye has its own group, so the blink scales it around its own centre */}
      <g data-part="eyes">
        <g data-part="eye-left">
          <ellipse
            cx="0.739602"
            cy="1.03389"
            rx="0.739602"
            ry="1.03389"
            transform="matrix(0.979378 -0.201885 0.202507 0.979312 5.48169 12.925)"
            fill={INK}
          />
        </g>
        <g data-part="eye-right">
          <ellipse
            cx="0.739602"
            cy="1.03389"
            rx="0.739602"
            ry="1.03389"
            transform="matrix(0.97934 -0.202071 0.20232 0.979351 10.7438 11.9134)"
            fill={INK}
          />
        </g>
      </g>
      <path
        d="M8.0552 16.2994C7.88402 16.0826 7.56954 16.0455 7.35279 16.2164C7.13604 16.3874 7.0991 16.7017 7.27028 16.9185L7.66274 16.609L8.0552 16.2994ZM12.2844 16.1603C12.4117 15.9154 12.3163 15.6136 12.0713 15.486C11.8262 15.3585 11.5243 15.4537 11.3969 15.6986L11.8406 15.9294L12.2844 16.1603ZM7.66274 16.609L7.27028 16.9185C7.9064 17.7241 9.01342 17.8702 9.93861 17.7003C10.8697 17.5295 11.8467 17.0018 12.2844 16.1603L11.8406 15.9294L11.3969 15.6986C11.1509 16.1714 10.5091 16.579 9.75726 16.717C8.99948 16.856 8.35937 16.6847 8.0552 16.2994L7.66274 16.609Z"
        fill={INK}
      />
    </svg>
  );
};
